import * as PIXI from 'pixi.js'
import Detector from 'utils/Detector'
import * as CSS from 'utils/css.js'
import * as Utils from 'utils/math.js'
import * as Canvas from 'utils/canvas.js'
import SimplexNoise from 'simplex-noise'
import Emitter from 'core/Emitter'

import Flower from 'components/Flower'
import FlowerParticles from 'components/FlowerParticles'

import Gui from 'core/Gui'

export default class SingleScene {
  constructor (opts = {}) {
    this.container = opts.container
    this.flowersData = opts.flowersData
    this.prefix = CSS.getPrefix()
    this.size = Detector.getScreenSize()
    this.simplex = new SimplexNoise()
    this.scrollY = {
      dest: 0,
      current: 0
    }
    this.isAlreadyInit = false

    this.params = {
      alphaMask: {
        value: 1,
        range: [0, 1]
      },
      debug: false,
      toggleMask: false,
      toggleBackground: false
    }
    this.wind = {
      force: {
        value: 5,
        range: [-45, 90]
      },
      speed: {
        value: 1,
        range: [-5, 10]
      }
    }
    this.toggleDebugMode()

    // regular scene creation
    this.scene = new PIXI.Container()

    let pixelRatio = window.devicePixelRatio || 1

    PIXI.settings.RESOLUTION = pixelRatio
    PIXI.settings.RENDER_OPTIONS.autoResize = false

    let noWebgl = Detector.isWindow7()

    // eslint-disable-next-line new-cap
    this.renderer = new PIXI.autoDetectRenderer(this.size.w, this.size.h, {
      transparent: true,
      autoResize: false,
      resolution: pixelRatio
    }, noWebgl)

    this.renderer.isCanvas = (this.renderer instanceof PIXI.CanvasRenderer)

    this.renderer.plugins.interaction.autoPreventDefault = false
    this.renderer.view.style['-ms-content-zooming'] = 'auto'
    this.renderer.view.style['-ms-touch-action'] = 'auto'
    this.renderer.view.style['touch-action'] = 'auto'

    this.container.appendChild(this.renderer.view)

    this.resources = window.loaderManager.resources

    setTimeout(() => {
      this.init()
    }, 0)
    // this.loadResources()
  }

  loadResources () {
    this.loader = PIXI.loader
    // this.loader.reset()

    this.loader.load((loader, resources) => {
      this.resources = resources
      this.loadSpritesheets()
    })
  }

  loadSpritesheets () {
    let flower = this.flowersData
    this.loader.add(`/static/assets/img/spritesheets/${flower.id}_${flower.title}.json`)

    this.loader.load((loader, resources) => {
      this.resources = resources
      this.bindAll()
      this.addListeners()
      this.createFlower()
    })
  }

  bindAll () {
    // ['getFilteredItems']
    //     .forEach((fn) => (this[fn] = this[fn].bind(this)))
  }

  addListeners () {
  }

  removeListeners () {
  }

  init () {
    this.bindAll()
    this.addListeners()
    this.createFlower()
  }

  createParticles () {
    this.flowerParticles = new FlowerParticles({
      parent: this
    })
  }

  createFlower () {
    let divFlowers
    let index = 0

    if (!this.isAlreadyInit) {
      divFlowers = document.createElement('div')
      divFlowers.classList.add('flowers')
      this.container.appendChild(divFlowers)

      this.createParticles()
    }

    this.flower = new Flower({
      isSingle: true,
      index: index,
      resources: this.resources,
      data: this.flowersData,
      scene: this.scene,
      renderer: this.renderer
    })

    if (!this.isAlreadyInit) {
      this.flowersDiv = document.querySelector('.flowers')
    }

    this.isReady = true

    Emitter.emit('GET_RESIZE')
  }

  /**
   * gui function
   */
  gui () {
    this.guiInstance = new Gui()
    this.guiInstance.panel
    .addGroup({ label: 'Mask', enable: true })
    .addSlider(this.params.alphaMask, 'value', 'range', {
      label: 'Alpha',
      dp: 3,
      onChange: () => {
        this.flowers.forEach((flower, i) => flower.setAlphaMask(this.params.alphaMask.value))
      }
    })
    .addGroup({ label: 'Wind', enable: false })
    .addSlider(this.wind.force, 'value', 'range', { label: 'Force ', dp: 1 })
    .addSlider(this.wind.speed, 'value', 'range', { label: 'Speed', dp: 3 })
    .addGroup({ label: 'Debug', enable: false })
    .addCheckbox(this.params, 'debug', {
      label: 'Debug mode',
      onChange: () => {
        this.toggleDebugMode()
      }
    })
    .addCheckbox(this.params, 'toggleMask', {
      label: 'Toggle mask',
      onChange: () => {
        if (this.params.toggleMask) {
          this.flowers.forEach((flower, i) => {
            flower.createMask()
          })
          this.resize()
        } else {
          this.flowers.forEach((flower, i) => flower.removeMask())
        }
      }
    })
    .addCheckbox(this.params, 'toggleBackground', {
      label: 'Toggle black background',
      onChange: () => {
        if (this.params.toggleBackground) {
          this.container.style.backgroundColor = '#000'
        } else {
          this.container.style.backgroundColor = ''
        }
      }
    })
  }

  toggleDebugMode () {
    if (this.params.debug) {
      this.container.classList.add('debug')
    } else {
      this.container.classList.remove('debug')
    }
  }

  scroll (vars) {
    if (!this.isReady) return
    this.scrollY.dest = vars.target
    this.scrollY.current = Utils.round(vars.current)

    // this.flowersDiv.style[this.prefix] = CSS.getTransform(-this.scrollY.current)
    // this.scene.position.y = -this.scrollY.current
  }

  resize (size) {
    this.size = size

    this.renderer.resize(this.size.w, this.size.h)
    this.renderer.view.style.width = `${this.size.w}px`
    this.renderer.view.style.height = `${this.size.h}px`

    if (this.isReady) {
      this.flower.resize(this.size)
    }
  }

  update (time) {
    if (this.isReady) {
      this.flowerParticles.update(time)
      this.flower.update({time: time, wind: this.wind, scrollY: this.scrollY})
    }

    this.renderer.render(this.scene)
  }

  updateFlower (data) {
    if (!this.flower) return
    this.isAlreadyInit = true

    // if (this.loaderUpdate) {
    //   this.loaderUpdate.reset()
    //   this.loaderUpdate = null
    // }

    this.flowersData = data

    // this.loaderUpdate = new PIXI.loaders.Loader()
    // this.loaderUpdate.add(`/static/assets/img/spritesheets/${this.flowersData.id}_${this.flowersData.title}.json`)

    if (this.renderer.isCanvas) {
      this.oldFlower = this.flower
      this.oldFlower.destroy()
      this.oldFlower = null
      this.isReady = true
    } else {
      if (this.oldFlower) {
        this.oldFlower.destroy()
        this.oldFlower = null
        this.isReady = true
      }

      this.oldFlower = this.flower
      this.oldFlower.hideMask(() => {
        if (this.oldFlower) {
          // this.isReady = false
          this.oldFlower.destroy()
          this.oldFlower = null
        }
      })
    }

    this.isReady = false
    this.createFlower()
  }

  destroy () {
    this.removeListeners()
    this.guiInstance && this.guiInstance.destroyGui()
    this.flowerParticles.destroy()
    this.flower.destroy()
    this.scene.destroy({
      children: true
    })
    this.renderer.destroy(true)
    // this.loader.reset()
  }

}



/** WEBPACK FOOTER **
 ** ./src/components/SinglePlant/classes/SingleScene.js
 **/